/**
 * Private perps vault operations.
 *
 * These authorise against the perps vault via its ownership proof, not via
 * Nova settlement — they lived in nova-operations.ts only by accident of
 * history, so they are kept here rather than removed with the Nova stack.
 */
/**
 * Nova Operations Module
 *
 * All privacy operations using Nova IVC + Groth16 compression.
 * This replaces the legacy snarkjs-based operations.
 */

import { ethers } from "ethers";
import { EVM_CONFIG } from "../config";
import { friendlyError } from "../base-app/utils/errors";
import {
  NoteTokenType,
  type SpendingKeys,
  type JoinSplitNote,
  type StoredJoinSplitNote,
} from "../joinsplit/types";
import {
  computeNPK,
  computeCommitment,
  computeNullifier,
} from "../joinsplit/keys";
import { getTotalValue, selectNotes } from "../joinsplit/notes";
import { getMerkleProof, insertMerkleCommitment } from "../utils/storage";
import {
  encryptInboxMessageWithPublicKey,
  formatInboxPublicKeyHex,
} from "../utils/inbox-crypto";
import {
  buildRecoveryNotePayload,
  deriveRecoveryKeyFromMpkHex,
} from "../utils/recovery";
import RelayerClient from "../utils/relayer-client";
import { createStoredProxyAuthHeaders } from "../utils/proxy-auth";
import { deriveBaseTokenHash } from "./base-token-hash";

const FIELD_PRIME = BigInt(
  "21888242871839275222246405745257275088548364400416034343698204186575808495617",
);
const ABI_CODER = ethers.AbiCoder.defaultAbiCoder();
type EvmTokenType = "erc20" | "erc721" | "erc1155";
export interface PerpsSponsoredPermissionParams {
  chainId?: number;
  vaultAddress: string;
  userAddress?: string;
  executor: string;
  permission: string; // bytes32 hex
  expiry: number; // unix seconds
  ownershipProof: string; // hex bytes
  onLog?: (
    message: string,
    type?: "info" | "success" | "error" | "warning",
    txHash?: string,
  ) => void;
}

export interface PerpsSponsoredEscapeParams {
  chainId?: number;
  vaultAddress: string;
  recipient: string;
  expiry: number; // unix seconds
  ownershipProof: string; // hex bytes
  onLog?: (
    message: string,
    type?: "info" | "success" | "error" | "warning",
    txHash?: string,
  ) => void;
}

export interface PerpsSponsoredResult {
  success: boolean;
  txHash?: string;
  status?: string;
  code?: string;
  recoveryAction?: string;
  recoveryRoute?: string;
  vaultAddress?: string;
  assetToken?: string;
  expectedOutputAmount?: string;
  actualOutputAmount?: string;
  withdrawTxHash?: string;
  depositTxHash?: string;
  outputAmount?: string;
  outputCommitment?: string;
  outputTokenHash?: string;
  leafIndex?: number;
  reshielded?: boolean;
  error?: string;
}

function perpsPrivateReshieldConfirmed(result: {
  reshielded?: boolean;
  depositTxHash?: string;
  outputAmount?: string;
  leafIndex?: number;
}): boolean {
  return (
    result.reshielded === true &&
    !!result.depositTxHash &&
    !!result.outputAmount &&
    typeof result.leafIndex === "number" &&
    result.leafIndex >= 0
  );
}

const PERPS_PRIVATE_RESHIELD_UNCONFIRMED_MESSAGE =
  "Private reshield is not confirmed onchain yet. Private balance will update only after the deposit transaction is confirmed; refresh vault balances before retrying.";

export interface PerpsSponsoredDeployParams {
  chainId?: number;
  vaultOwner: string;
  accountNft?: string;
  accountTokenId?: number;
  accountId?: number;
  permissionManager?: string;
  ownershipVerifier: string;
  ownershipCommitment: string; // bytes32 hex
  perpsMarketProxy?: string;
  deploySalt?: string;
  autoCreateAccount?: boolean;
  requestedAccountId?: number;
  onLog?: (
    message: string,
    type?: "info" | "success" | "error" | "warning",
    txHash?: string,
  ) => void;
}

export interface PerpsSponsoredCreateAccountResult extends PerpsSponsoredResult {
  vaultAddress?: string;
  accountNft?: string;
  accountTokenId?: string;
  accountId?: string;
  accountCreateTxHash?: string;
}

export interface PerpsSponsoredTradeParams {
  chainId?: number;
  vaultAddress: string;
  userAddress?: string;
  action: "commit" | "cancel";
  actionCallData: string; // hex bytes
  expiry: number;
  ownershipProof: string;
  onLog?: (
    message: string,
    type?: "info" | "success" | "error" | "warning",
    txHash?: string,
  ) => void;
}

export interface PerpsSponsoredMarginParams {
  chainId?: number;
  vaultAddress: string;
  userAddress?: string;
  direction: "deposit" | "withdraw";
  modifyCollateralCallData: string; // hex bytes
  feeCollateralToken?: string;
  withdrawRecipient?: string;
  spotSynthMarketId?: number;
  privateReshield?: boolean;
  outputNpk?: string;
  outputTokenHash?: string;
  outputCommitment?: string;
  outputRecoveryKey?: string;
  outputEncryptedNote?: string;
  expectedOutputAmount?: string;
  reshieldOwnershipProof?: string;
  expiry: number;
  ownershipProof: string;
  onLog?: (
    message: string,
    type?: "info" | "success" | "error" | "warning",
    txHash?: string,
  ) => void;
}

export interface PerpsSponsoredVaultWithdrawParams {
  chainId?: number;
  vaultAddress: string;
  userAddress?: string;
  feeCollateralToken?: string;
  outputNpk?: string;
  outputTokenHash?: string;
  outputCommitment?: string;
  outputRecoveryKey?: string;
  outputEncryptedNote?: string;
  expectedOutputAmount?: string;
  expiry: number;
  ownershipProof: string;
  onLog?: (
    message: string,
    type?: "info" | "success" | "error" | "warning",
    txHash?: string,
  ) => void;
}

export async function executePerpsDeployVault({
  chainId = EVM_CONFIG.CHAIN_ID,
  vaultOwner,
  accountNft,
  accountTokenId,
  accountId,
  permissionManager,
  ownershipVerifier,
  ownershipCommitment,
  perpsMarketProxy,
  deploySalt,
  onLog,
}: PerpsSponsoredDeployParams): Promise<PerpsSponsoredResult> {
  try {
    onLog?.("Submitting sponsored perps vault deploy...", "info");
    const result = await RelayerClient.submitPerpsDeploy({
      chainId,
      vaultOwner,
      accountNft,
      accountTokenId,
      accountId,
      permissionManager,
      ownershipVerifier,
      ownershipCommitment,
      perpsMarketProxy,
      deploySalt,
    });
    onLog?.("Perps vault deploy confirmed", "success", result.transactionHash);
    return { success: true, txHash: result.transactionHash };
  } catch (err) {
    const msg = friendlyError(err);
    onLog?.(`Perps deploy failed: ${msg}`, "error");
    return { success: false, error: msg };
  }
}

export async function executePerpsCreateAccount({
  chainId = EVM_CONFIG.CHAIN_ID,
  vaultOwner,
  accountNft,
  accountTokenId,
  accountId,
  permissionManager,
  ownershipVerifier,
  ownershipCommitment,
  autoCreateAccount,
  requestedAccountId,
  perpsMarketProxy,
  deploySalt,
  onLog,
}: PerpsSponsoredDeployParams): Promise<PerpsSponsoredCreateAccountResult> {
  try {
    onLog?.("Creating private perps account (sponsored)...", "info");
    const result = await RelayerClient.submitPerpsCreateAccount({
      chainId,
      vaultOwner,
      accountNft,
      accountTokenId,
      accountId,
      permissionManager,
      ownershipVerifier,
      ownershipCommitment,
      autoCreateAccount,
      requestedAccountId,
      perpsMarketProxy,
      deploySalt,
    });
    onLog?.("Private perps account created", "success", result.transactionHash);
    return {
      success: true,
      txHash: result.transactionHash,
      vaultAddress: result.vaultAddress,
      accountNft: result.accountNft,
      accountTokenId: result.accountTokenId,
      accountId: result.accountId,
      accountCreateTxHash: result.accountCreateTxHash,
    };
  } catch (err) {
    const msg = friendlyError(err);
    onLog?.(`Perps create-account failed: ${msg}`, "error");
    return { success: false, error: msg };
  }
}

export async function executePerpsDelegatePermission({
  chainId = EVM_CONFIG.CHAIN_ID,
  vaultAddress,
  userAddress,
  executor,
  permission,
  expiry,
  ownershipProof,
  onLog,
}: PerpsSponsoredPermissionParams): Promise<PerpsSponsoredResult> {
  try {
    onLog?.("Submitting sponsored perps permission grant...", "info");
    const result = await RelayerClient.submitPerpsDelegate({
      chainId,
      vaultAddress,
      userAddress,
      executor,
      permission,
      expiry,
      ownershipProof,
    });
    onLog?.(
      "Perps permission grant confirmed",
      "success",
      result.transactionHash,
    );
    return { success: true, txHash: result.transactionHash };
  } catch (err) {
    const msg = friendlyError(err);
    onLog?.(`Perps delegate failed: ${msg}`, "error");
    return { success: false, error: msg };
  }
}

export async function executePerpsRevokePermission({
  chainId = EVM_CONFIG.CHAIN_ID,
  vaultAddress,
  userAddress,
  executor,
  permission,
  expiry,
  ownershipProof,
  onLog,
}: PerpsSponsoredPermissionParams): Promise<PerpsSponsoredResult> {
  try {
    onLog?.("Submitting sponsored perps permission revoke...", "info");
    const result = await RelayerClient.submitPerpsRevoke({
      chainId,
      vaultAddress,
      userAddress,
      executor,
      permission,
      expiry,
      ownershipProof,
    });
    onLog?.(
      "Perps permission revoke confirmed",
      "success",
      result.transactionHash,
    );
    return { success: true, txHash: result.transactionHash };
  } catch (err) {
    const msg = friendlyError(err);
    onLog?.(`Perps revoke failed: ${msg}`, "error");
    return { success: false, error: msg };
  }
}

export async function executePerpsEscapeTransfer({
  chainId = EVM_CONFIG.CHAIN_ID,
  vaultAddress,
  recipient,
  expiry,
  ownershipProof,
  onLog,
}: PerpsSponsoredEscapeParams): Promise<PerpsSponsoredResult> {
  try {
    onLog?.("Submitting sponsored perps escape transfer...", "info");
    const result = await RelayerClient.submitPerpsEscape({
      chainId,
      vaultAddress,
      recipient,
      expiry,
      ownershipProof,
    });
    onLog?.(
      "Perps escape transfer confirmed",
      "success",
      result.transactionHash,
    );
    return { success: true, txHash: result.transactionHash };
  } catch (err) {
    const msg = friendlyError(err);
    onLog?.(`Perps escape failed: ${msg}`, "error");
    return { success: false, error: msg };
  }
}

export async function executePerpsTrade({
  chainId = EVM_CONFIG.CHAIN_ID,
  vaultAddress,
  userAddress,
  action,
  actionCallData,
  expiry,
  ownershipProof,
  onLog,
}: PerpsSponsoredTradeParams): Promise<PerpsSponsoredResult> {
  try {
    onLog?.(`Submitting sponsored perps ${action}...`, "info");
    const result = await RelayerClient.submitPerpsTrade({
      chainId,
      vaultAddress,
      userAddress,
      action,
      actionCallData,
      expiry,
      ownershipProof,
    });
    onLog?.(`Perps ${action} confirmed`, "success", result.transactionHash);
    return { success: true, txHash: result.transactionHash };
  } catch (err) {
    const msg = friendlyError(err);
    onLog?.(`Perps ${action} failed: ${msg}`, "error");
    return { success: false, error: msg };
  }
}

export async function executePerpsMargin({
  chainId = EVM_CONFIG.CHAIN_ID,
  vaultAddress,
  userAddress,
  direction,
  modifyCollateralCallData,
  feeCollateralToken,
  withdrawRecipient,
  spotSynthMarketId,
  privateReshield,
  outputNpk,
  outputTokenHash,
  outputCommitment,
  outputRecoveryKey,
  outputEncryptedNote,
  expectedOutputAmount,
  reshieldOwnershipProof,
  expiry,
  ownershipProof,
  onLog,
}: PerpsSponsoredMarginParams): Promise<PerpsSponsoredResult> {
  try {
    onLog?.(`Submitting sponsored perps margin ${direction}...`, "info");
    const result = await RelayerClient.submitPerpsMargin({
      chainId,
      vaultAddress,
      userAddress,
      direction,
      modifyCollateralCallData,
      feeCollateralToken,
      withdrawRecipient,
      spotSynthMarketId,
      privateReshield,
      outputNpk,
      outputTokenHash,
      outputCommitment,
      outputRecoveryKey,
      outputEncryptedNote,
      expectedOutputAmount,
      reshieldOwnershipProof,
      expiry,
      ownershipProof,
    });
    if (!result.success) {
      return {
        success: false,
        error: result.error || "Perps margin update failed",
        txHash: result.transactionHash,
        status: result.status,
        code: result.code,
        recoveryAction: result.recoveryAction,
        recoveryRoute: result.recoveryRoute,
        vaultAddress: result.vaultAddress,
        assetToken: result.assetToken,
        expectedOutputAmount: result.expectedOutputAmount,
        actualOutputAmount: result.actualOutputAmount,
        withdrawTxHash: result.withdrawTxHash,
        outputAmount: result.outputAmount,
        outputCommitment: result.outputCommitment,
        outputTokenHash: result.outputTokenHash,
        reshielded: result.reshielded,
      };
    }
    if (privateReshield && !perpsPrivateReshieldConfirmed(result)) {
      return {
        success: false,
        error: PERPS_PRIVATE_RESHIELD_UNCONFIRMED_MESSAGE,
        txHash: result.transactionHash,
        status: result.status,
        code: result.code,
        withdrawTxHash: result.withdrawTxHash,
        depositTxHash: result.depositTxHash,
      };
    }
    onLog?.(
      `Perps margin ${direction} confirmed`,
      "success",
      result.transactionHash,
    );
    return {
      success: true,
      txHash: result.transactionHash,
      status: result.status,
      code: result.code,
      recoveryAction: result.recoveryAction,
      recoveryRoute: result.recoveryRoute,
      vaultAddress: result.vaultAddress,
      assetToken: result.assetToken,
      expectedOutputAmount: result.expectedOutputAmount,
      actualOutputAmount: result.actualOutputAmount,
      withdrawTxHash: result.withdrawTxHash,
      depositTxHash: result.depositTxHash,
      outputAmount: result.outputAmount,
      outputCommitment: result.outputCommitment,
      outputTokenHash: result.outputTokenHash,
      leafIndex: result.leafIndex,
      reshielded: result.reshielded,
    };
  } catch (err) {
    const msg = friendlyError(err);
    onLog?.(`Perps margin ${direction} failed: ${msg}`, "error");
    return { success: false, error: msg };
  }
}

export async function executePerpsVaultWithdraw({
  chainId = EVM_CONFIG.CHAIN_ID,
  vaultAddress,
  userAddress,
  feeCollateralToken,
  outputNpk,
  outputTokenHash,
  outputCommitment,
  outputRecoveryKey,
  outputEncryptedNote,
  expectedOutputAmount,
  expiry,
  ownershipProof,
  onLog,
}: PerpsSponsoredVaultWithdrawParams): Promise<PerpsSponsoredResult> {
  try {
    onLog?.("Submitting sponsored perps vault withdraw...", "info");
    const result = await RelayerClient.submitPerpsVaultWithdraw({
      chainId,
      vaultAddress,
      userAddress,
      feeCollateralToken,
      outputNpk,
      outputTokenHash,
      outputCommitment,
      outputRecoveryKey,
      outputEncryptedNote,
      expectedOutputAmount,
      expiry,
      ownershipProof,
    });
    if (!perpsPrivateReshieldConfirmed(result)) {
      return {
        success: false,
        error: PERPS_PRIVATE_RESHIELD_UNCONFIRMED_MESSAGE,
        txHash: result.transactionHash,
        withdrawTxHash: result.withdrawTxHash,
        depositTxHash: result.depositTxHash,
      };
    }
    onLog?.(
      "Perps vault withdraw confirmed",
      "success",
      result.transactionHash,
    );
    return {
      success: true,
      txHash: result.transactionHash,
      withdrawTxHash: result.withdrawTxHash,
      depositTxHash: result.depositTxHash,
      outputAmount: result.outputAmount,
      outputCommitment: result.outputCommitment,
      outputTokenHash: result.outputTokenHash,
      leafIndex: result.leafIndex,
      reshielded: result.reshielded,
    };
  } catch (err) {
    const msg = friendlyError(err);
    onLog?.(`Perps vault withdraw failed: ${msg}`, "error");
    return { success: false, error: msg };
  }
}
